/**
 * DetectionsTable Component
 *
 * Displays detection results grouped by room > section > class in an expandable table format.
 * Default view shows class + quantity. Toggle to expand and see individual detections.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, ChevronRight, User, Bot, Image as ImageIcon, Trash2, Eye, EyeOff, Package } from 'lucide-react'
import { Room } from '@/types'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { apiService } from '@/services/api'
import { useTranslation } from 'react-i18next'
import { formatClassNameForDisplay } from '@/utils/classNameUtils'
import { getClassColor } from '@/utils/colorUtils'

export interface DetectionRow {
  id: number
  sessionId: number
  analysisId: number
  roomName: string
  sectionId: number | null
  sectionName: string | null
  imageName: string
  bbox: [number, number, number, number]
  topClass: string
  confidence: number
  classificationConfidence: number
  qualityScore?: number
  isUserCorrected: boolean
  userCorrectedClass?: string
  originalTopClass?: string
  isUserDrawn: boolean
  timestamp: string
  uniqueId: string
  orderNumber?: number
  hasUncertainDetections: boolean
}

export interface ClassAggregation {
  className: string
  rawClassId: string  // Original class ID for category/thumbnail lookups
  quantity: number
  detections: DetectionRow[]
  avgConfidence: number
  minConfidence: number
  maxConfidence: number
  userCorrectedCount: number
  userDrawnCount: number
}

export interface SectionGroup {
  sectionId: number | null
  sectionName: string | null
  detections: DetectionRow[]
  classAggregations?: ClassAggregation[]
  isExpanded: boolean
}

export interface RoomGroup {
  room: Room
  sections: SectionGroup[]
  isExpanded: boolean
}

// Get the raw class ID for API lookups (uses originalTopClass or converts display name)
const getRawClassId = (detection: DetectionRow): string => {
  // originalTopClass is the raw folder name from the backend
  return detection.originalTopClass || detection.topClass.replace(/ /g, '_')
}

// Convert display name back to folder name format for API lookups
const toFolderName = (displayName: string): string => {
  return displayName.replace(/ /g, '_')
}

// Image Preview Canvas Component - draws image with bounding box and label (NO score)
const ImagePreviewCanvas: React.FC<{
  imageUrl: string
  detections: DetectionRow[]
  getClassCategory: (classId: string) => any
}> = ({ imageUrl, detections, getClassCategory }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const img = imgRef.current
    if (!canvas || !img) return

    const drawBoundingBoxes = () => {
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const containerWidth = Math.floor(window.innerWidth * 0.55) - 16
      const containerHeight = Math.floor(window.innerHeight * 0.55) - 16
      canvas.width = containerWidth
      canvas.height = containerHeight

      const imgAspect = img.naturalWidth / img.naturalHeight
      const containerAspect = containerWidth / containerHeight

      let drawWidth, drawHeight, offsetX = 0, offsetY = 0

      if (imgAspect > containerAspect) {
        drawWidth = containerWidth
        drawHeight = containerWidth / imgAspect
        offsetY = (containerHeight - drawHeight) / 2
      } else {
        drawHeight = containerHeight
        drawWidth = containerHeight * imgAspect
        offsetX = (containerWidth - drawWidth) / 2
      }

      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)

      // Draw all detections
      detections.forEach((detection) => {
        const [x1, y1, x2, y2] = detection.bbox
        const scaleX = drawWidth / img.naturalWidth
        const scaleY = drawHeight / img.naturalHeight

        const boxX = offsetX + x1 * scaleX
        const boxY = offsetY + y1 * scaleY
        const boxWidth = (x2 - x1) * scaleX
        const boxHeight = (y2 - y1) * scaleY

        // Try multiple formats for category lookup
        const rawId = getRawClassId(detection)
        const withSpaces = detection.topClass.replace(/_/g, ' ')
        const category = getClassCategory(rawId)
          || getClassCategory(detection.topClass)
          || getClassCategory(withSpaces)
        const color = category?.color || getClassColor(rawId, category)

        // Draw bounding box
        ctx.strokeStyle = color
        ctx.lineWidth = 3
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight)

        // Draw label background
        const label = formatClassNameForDisplay(detection.topClass)
        ctx.font = 'bold 12px sans-serif'
        const labelWidth = ctx.measureText(label).width + 10
        const labelHeight = 20

        ctx.fillStyle = color
        ctx.fillRect(boxX, boxY - labelHeight, labelWidth, labelHeight)

        // Draw label text
        ctx.fillStyle = 'white'
        ctx.fillText(label, boxX + 5, boxY - 6)
      })
    }

    img.onload = drawBoundingBoxes
    if (img.complete) drawBoundingBoxes()
  }, [imageUrl, detections, getClassCategory])

  return (
    <div className="relative w-full h-full">
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Detection preview"
        className="hidden"
        crossOrigin="anonymous"
      />
      <canvas
        ref={canvasRef}
        className="w-full h-full object-contain border rounded-lg"
      />
    </div>
  )
}

// Thumbnail tooltip component with actual image loading
const ClassThumbnailTooltip: React.FC<{
  className: string
  quantity: number
  thumbnailUrl?: string | null
}> = ({ className, quantity, thumbnailUrl }) => {
  const [imgLoaded, setImgLoaded] = useState(false)
  const [imgError, setImgError] = useState(false)

  return (
    <div className="text-xs w-36">
      <div className="font-medium text-white mb-1">{formatClassNameForDisplay(className)}</div>
      <div className="text-gray-400 mb-2">
        {quantity} detection{quantity !== 1 ? 's' : ''}
      </div>
      {thumbnailUrl && !imgError ? (
        <div className="relative">
          {!imgLoaded && (
            <div className="w-32 h-32 bg-gray-700 rounded flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
            </div>
          )}
          <img
            src={thumbnailUrl}
            alt={className}
            className={`w-32 h-32 object-cover rounded border border-gray-600 ${imgLoaded ? '' : 'hidden'}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
        </div>
      ) : (
        <div className="w-32 h-20 bg-gray-700 rounded flex items-center justify-center">
          <Package className="h-6 w-6 text-gray-500" />
        </div>
      )}
    </div>
  )
}

interface DetectionsTableProps {
  roomGroups: RoomGroup[]
  onRoomGroupsChange: (groups: RoomGroup[]) => void
  loading: boolean
  showDetails?: boolean
  onShowDetailsChange?: (show: boolean) => void
  isAdmin?: boolean
}

const DetectionsTable: React.FC<DetectionsTableProps> = ({
  roomGroups,
  onRoomGroupsChange,
  loading,
  showDetails = false,
  onShowDetailsChange,
  isAdmin = false,
}) => {
  const { t } = useTranslation()
  const { getClassCategory, getClassThumbnail } = useCategoryContext()
  const [imageCache] = useState(new Map<number, string>())
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewDetections, setPreviewDetections] = useState<DetectionRow[]>([])
  const [showPreview, setShowPreview] = useState(false)

  // Track expanded classes locally
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set())

  // Compute class aggregations for each section
  const roomGroupsWithAggregations = useMemo(() => {
    return roomGroups.map(group => ({
      ...group,
      sections: group.sections.map(section => {
        const classMap = new Map<string, DetectionRow[]>()
        section.detections.forEach(detection => {
          const className = detection.topClass
          if (!classMap.has(className)) {
            classMap.set(className, [])
          }
          classMap.get(className)!.push(detection)
        })

        const classAggregations: ClassAggregation[] = Array.from(classMap.entries())
          .map(([className, detections]) => {
            const confidences = detections.map(d => d.classificationConfidence)
            // Get raw class ID from first detection for category/thumbnail lookups
            const rawClassId = getRawClassId(detections[0])
            return {
              className,
              rawClassId,
              quantity: detections.length,
              detections,
              avgConfidence: confidences.reduce((a, b) => a + b, 0) / confidences.length,
              minConfidence: Math.min(...confidences),
              maxConfidence: Math.max(...confidences),
              userCorrectedCount: detections.filter(d => d.isUserCorrected).length,
              userDrawnCount: detections.filter(d => d.isUserDrawn).length,
            }
          })
          .sort((a, b) => b.quantity - a.quantity)

        return {
          ...section,
          classAggregations,
        }
      })
    }))
  }, [roomGroups])

  const toggleRoomExpansion = useCallback((roomId: number) => {
    onRoomGroupsChange(
      roomGroups.map((group) =>
        group.room.id === roomId
          ? { ...group, isExpanded: !group.isExpanded }
          : group
      )
    )
  }, [roomGroups, onRoomGroupsChange])

  const toggleSectionExpansion = useCallback((roomId: number, sectionId: number | null) => {
    onRoomGroupsChange(
      roomGroups.map((group) =>
        group.room.id === roomId
          ? {
              ...group,
              sections: group.sections.map((section) =>
                section.sectionId === sectionId
                  ? { ...section, isExpanded: !section.isExpanded }
                  : section
              )
            }
          : group
      )
    )
  }, [roomGroups, onRoomGroupsChange])

  const toggleClassExpansion = useCallback((roomId: number, sectionId: number | null, className: string) => {
    const key = `${roomId}-${sectionId}-${className}`
    setExpandedClasses(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const isClassExpanded = useCallback((roomId: number, sectionId: number | null, className: string) => {
    return expandedClasses.has(`${roomId}-${sectionId}-${className}`)
  }, [expandedClasses])

  // Show preview for a single detection
  const handleSingleImagePreview = useCallback(
    async (detection: DetectionRow) => {
      setPreviewDetections([detection])

      if (imageCache.has(detection.analysisId)) {
        setPreviewImage(imageCache.get(detection.analysisId)!)
        setPreviewLoading(false)
        setShowPreview(true)
        return
      }

      setPreviewLoading(true)
      setShowPreview(true)

      try {
        const imageBlob = await apiService.getAnalysisImageBlob(detection.analysisId)
        const imageUrl = URL.createObjectURL(imageBlob)
        imageCache.set(detection.analysisId, imageUrl)
        setPreviewImage(imageUrl)
      } catch (error) {
        console.error('Failed to load preview image:', error)
        setPreviewImage(null)
      } finally {
        setPreviewLoading(false)
      }
    },
    [imageCache]
  )

  // Show preview for aggregated class (all detections of same class from same image)
  const handleAggregatedImagePreview = useCallback(
    async (detections: DetectionRow[]) => {
      if (detections.length === 0) return

      // Group by analysisId and show first image with all its detections
      const firstDetection = detections[0]
      const sameImageDetections = detections.filter(d => d.analysisId === firstDetection.analysisId)

      setPreviewDetections(sameImageDetections)

      if (imageCache.has(firstDetection.analysisId)) {
        setPreviewImage(imageCache.get(firstDetection.analysisId)!)
        setPreviewLoading(false)
        setShowPreview(true)
        return
      }

      setPreviewLoading(true)
      setShowPreview(true)

      try {
        const imageBlob = await apiService.getAnalysisImageBlob(firstDetection.analysisId)
        const imageUrl = URL.createObjectURL(imageBlob)
        imageCache.set(firstDetection.analysisId, imageUrl)
        setPreviewImage(imageUrl)
      } catch (error) {
        console.error('Failed to load preview image:', error)
        setPreviewImage(null)
      } finally {
        setPreviewLoading(false)
      }
    },
    [imageCache]
  )

  const closePreview = useCallback(() => {
    setShowPreview(false)
    setPreviewImage(null)
    setPreviewDetections([])
  }, [])

  const deleteDetection = useCallback(
    async (detection: DetectionRow) => {
      if (!confirm('Delete this detection?')) return

      try {
        await apiService.deleteDetection(
          detection.sessionId,
          detection.analysisId,
          detection.id
        )

        const newGroups = roomGroups
          .map((group) => ({
            ...group,
            sections: group.sections
              .map((section) => ({
                ...section,
                detections: section.detections
                  .filter((d) => d.uniqueId !== detection.uniqueId)
                  .map((d, index) => ({ ...d, orderNumber: index + 1 })),
              }))
              .filter((section) => section.detections.length > 0),
          }))
          .filter((group) => group.sections.length > 0)

        onRoomGroupsChange(newGroups)
      } catch (error) {
        console.error('Failed to delete detection:', error)
      }
    },
    [roomGroups, onRoomGroupsChange]
  )

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading detections...</span>
        </div>
      </div>
    )
  }

  if (roomGroups.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No detections found</h3>
        <p className="text-sm text-gray-500">Select a date and rooms to view detection results</p>
      </div>
    )
  }

  const totalDetections = roomGroups.reduce(
    (sum, group) => sum + group.sections.reduce((sSum, section) => sSum + section.detections.length, 0),
    0
  )

  const uniqueClasses = new Set<string>()
  roomGroups.forEach(group => {
    group.sections.forEach(section => {
      section.detections.forEach(detection => {
        uniqueClasses.add(detection.topClass)
      })
    })
  })

  return (
    <div className="space-y-3">
      {/* Image Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          onClick={closePreview}
        >
          <div
            className="bg-white border rounded-xl shadow-2xl p-3"
            style={{
              width: '60vw',
              height: '60vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : previewImage ? (
              <ImagePreviewCanvas
                imageUrl={previewImage}
                detections={previewDetections}
                getClassCategory={getClassCategory}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Failed to load image
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary header with toggle */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center gap-4">
          <span>{totalDetections} detection{totalDetections !== 1 ? 's' : ''}</span>
          <span className="text-gray-300">|</span>
          <span>{uniqueClasses.size} unique class{uniqueClasses.size !== 1 ? 'es' : ''}</span>
          <span className="text-gray-300">|</span>
          <span>{roomGroups.length} room{roomGroups.length !== 1 ? 's' : ''}</span>
        </div>

        {onShowDetailsChange && (
          <button
            onClick={() => onShowDetailsChange(!showDetails)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              showDetails
                ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {showDetails ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                Summary View
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                Detail View
              </>
            )}
          </button>
        )}
      </div>

      {/* Room groups */}
      {roomGroupsWithAggregations.map((group) => (
        <div
          key={group.room.id}
          className="bg-white rounded-xl shadow-sm border overflow-hidden"
        >
          {/* Room Header */}
          <button
            onClick={() => toggleRoomExpansion(group.room.id)}
            className="w-full px-4 py-3 bg-gray-100 border-b flex items-center justify-between hover:bg-gray-150 transition-colors"
          >
            <div className="flex items-center gap-2">
              {group.isExpanded ? (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-600" />
              )}
              <h3 className="font-semibold text-gray-900">{group.room.name}</h3>
            </div>
            <span className="text-sm text-gray-500 bg-white px-2.5 py-0.5 rounded-full border">
              {group.sections.reduce((sum, s) => sum + s.detections.length, 0)} detection{group.sections.reduce((sum, s) => sum + s.detections.length, 0) !== 1 ? 's' : ''}
            </span>
          </button>

          {/* Sections within Room */}
          {group.isExpanded && (
            <div>
              {group.sections.map((section) => (
                <div key={section.sectionId ?? 'unassigned'} className="border-b last:border-b-0">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSectionExpansion(group.room.id, section.sectionId)}
                    className="w-full px-4 py-2 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2 ml-4">
                      {section.isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        {section.sectionName || 'Unassigned'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {section.classAggregations?.length || 0} class{(section.classAggregations?.length || 0) !== 1 ? 'es' : ''}
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                        {section.detections.length}
                      </span>
                    </div>
                  </button>

                  {/* Section Table */}
                  {section.isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-100 bg-white">
                            {showDetails && (
                              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-8">
                              </th>
                            )}
                            <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                              SKU / Class
                            </th>
                            <th className="px-4 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-20">
                              Qty
                            </th>
                            <th className="px-4 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-24">
                              SKU Source
                            </th>
                            <th className="px-4 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-24">
                              Detection
                            </th>
                            <th className="px-4 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-16">
                              Preview
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {section.classAggregations?.map((agg) => {
                            // Try multiple formats for category/thumbnail lookups
                            // Sometimes the class name comes with underscores, sometimes with spaces
                            const withUnderscores = agg.className.replace(/ /g, '_')
                            const withSpaces = agg.className.replace(/_/g, ' ')
                            const category = getClassCategory(agg.rawClassId)
                              || getClassCategory(agg.className)
                              || getClassCategory(withUnderscores)
                              || getClassCategory(withSpaces)

                            const color = category?.color || getClassColor(agg.rawClassId, category)
                            const thumbnail = getClassThumbnail(agg.rawClassId)
                              || getClassThumbnail(agg.className)
                              || getClassThumbnail(withUnderscores)
                              || getClassThumbnail(withSpaces)
                            const expanded = showDetails && isClassExpanded(group.room.id, section.sectionId, agg.className)

                            return (
                              <React.Fragment key={agg.className}>
                                {/* Aggregated Class Row - clickable anywhere in detail view */}
                                <tr
                                  className={`transition-colors hover:bg-gray-50/50 ${expanded ? 'bg-primary-50/30' : ''} ${showDetails ? 'cursor-pointer' : ''}`}
                                  onClick={showDetails ? () => toggleClassExpansion(group.room.id, section.sectionId, agg.className) : undefined}
                                >
                                  {showDetails && (
                                    <td className="px-4 py-2">
                                      {expanded ? (
                                        <ChevronDown className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                      )}
                                    </td>
                                  )}
                                  <td className="px-4 py-2">
                                    <div className="group relative inline-block">
                                      <span
                                        className="inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-medium border cursor-help"
                                        style={{
                                          borderColor: color,
                                          backgroundColor: `${color}10`,
                                          color: color,
                                        }}
                                      >
                                        <Package className="h-3.5 w-3.5 mr-1.5 opacity-60" />
                                        <span className="truncate max-w-[250px]">
                                          {formatClassNameForDisplay(agg.className)}
                                        </span>
                                      </span>
                                      {/* Tooltip */}
                                      <div className="absolute left-full ml-2 top-0 z-50 hidden group-hover:block pointer-events-none">
                                        <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg">
                                          <ClassThumbnailTooltip
                                            className={agg.className}
                                            quantity={agg.quantity}
                                            thumbnailUrl={thumbnail?.url}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                    <span className="inline-flex items-center justify-center min-w-[2rem] px-2.5 py-0.5 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">
                                      {agg.quantity}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-center gap-1">
                                      {agg.userCorrectedCount > 0 ? (
                                        <>
                                          <User className="h-3.5 w-3.5 text-blue-500" />
                                          <span className="text-xs text-gray-600">{agg.userCorrectedCount}</span>
                                        </>
                                      ) : (
                                        <>
                                          <Bot className="h-3.5 w-3.5 text-emerald-500" />
                                          <span className="text-xs text-gray-600">AI</span>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-center gap-1">
                                      {agg.userDrawnCount > 0 ? (
                                        <>
                                          <User className="h-3.5 w-3.5 text-violet-500" />
                                          <span className="text-xs text-gray-600">{agg.userDrawnCount}</span>
                                        </>
                                      ) : (
                                        <>
                                          <Bot className="h-3.5 w-3.5 text-emerald-500" />
                                          <span className="text-xs text-gray-600">AI</span>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={() => handleAggregatedImagePreview(agg.detections)}
                                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                      title="Preview detections"
                                    >
                                      <ImageIcon className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>

                                {/* Expanded Individual Detections */}
                                {expanded && agg.detections.map((detection, idx) => (
                                  <tr
                                    key={detection.uniqueId}
                                    className="bg-gray-50/50 border-l-4 border-l-primary-200"
                                  >
                                    <td className="px-4 py-1.5 text-xs text-gray-400 text-right">
                                      {idx + 1}
                                    </td>
                                    <td className="px-4 py-1.5 pl-6">
                                      <span className="text-xs text-gray-500">
                                        {detection.imageName.length > 35
                                          ? detection.imageName.substring(0, 35) + '...'
                                          : detection.imageName}
                                      </span>
                                    </td>
                                    <td className="px-4 py-1.5" colSpan={isAdmin ? 1 : 3}>
                                      {isAdmin ? (
                                        <div className="flex items-center justify-center gap-2">
                                          <span
                                            className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                                              detection.confidence >= 0.8
                                                ? 'bg-green-100 text-green-700'
                                                : detection.confidence >= 0.6
                                                  ? 'bg-amber-100 text-amber-700'
                                                  : 'bg-red-100 text-red-700'
                                            }`}
                                            title="Detection confidence"
                                          >
                                            D: {(detection.confidence * 100).toFixed(0)}%
                                          </span>
                                          <span
                                            className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                                              detection.classificationConfidence >= 0.8
                                                ? 'bg-green-100 text-green-700'
                                                : detection.classificationConfidence >= 0.6
                                                  ? 'bg-amber-100 text-amber-700'
                                                  : 'bg-red-100 text-red-700'
                                            }`}
                                            title="Classification confidence"
                                          >
                                            C: {(detection.classificationConfidence * 100).toFixed(0)}%
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center">
                                          <span className="text-xs text-gray-400">—</span>
                                        </div>
                                      )}
                                    </td>
                                    {isAdmin && (
                                      <>
                                        <td className="px-4 py-1.5 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            {detection.isUserCorrected ? (
                                              <User className="h-3 w-3 text-blue-500" />
                                            ) : (
                                              <Bot className="h-3 w-3 text-emerald-500" />
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-1.5 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            {detection.isUserDrawn ? (
                                              <User className="h-3 w-3 text-violet-500" />
                                            ) : (
                                              <Bot className="h-3 w-3 text-emerald-500" />
                                            )}
                                          </div>
                                        </td>
                                      </>
                                    )}
                                    <td className="px-4 py-1.5" colSpan={isAdmin ? undefined : 1}>
                                      <div className="flex items-center justify-end gap-1">
                                        <button
                                          onClick={() => handleSingleImagePreview(detection)}
                                          className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                                        >
                                          <ImageIcon className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          onClick={() => deleteDetection(detection)}
                                          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                          title="Delete detection"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default DetectionsTable
